import { SaveOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Collapse,
  Input,
  InputNumber,
  message,
  Row,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd';
import {
  DonorStatus,
  UserRole,
  useGetRegionsQuery,
  useGetUsersListLazyQuery,
} from 'gql/types';
import {
  DEFAULT_ATTN_SCORE_THRESHOLD,
  DEFAULT_ATTN_SCORE_WEIGHTS,
  DEFAULT_HEALTH_FLAG_THRESHOLDS,
  IAttnScoreWeights,
  IHealthFlagRange,
  IHealthFlagThresholds,
} from 'modules/dashboard/utils/healthFlags';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import HealthFlagSlider from './HealthFlagSlider';

import { SAVE_APP_SETTINGS_MUTATION } from '../../graphql/mutations';
import { GET_APP_SETTINGS_QUERY } from '../../graphql/queries';

const { Title, Text } = Typography;

const SETTINGS_TAB = 'ops-dashboard';

const ALL_USER_ROLES: UserRole[] = Object.values(UserRole);
const ALL_DONOR_STATUSES: DonorStatus[] = Object.values(DonorStatus);

const DEFAULT_ACTIVE_DONOR_STATUSES: DonorStatus[] = [
  DonorStatus.Active,
  DonorStatus.Approved,
  DonorStatus.AnnualLabworkDue,
];

const OPS_TAB_KEYS = [
  'overview',
  'phlebotomist',
  'recruiter',
  'handler',
  'drilldown',
  'trends',
] as const;

const OPS_TAB_LABELS: Record<string, string> = {
  overview: 'Overview',
  phlebotomist: 'Phlebotomist',
  recruiter: 'Recruiter',
  handler: 'Handler',
  drilldown: 'Drilldown',
  trends: 'Trends',
};

type TabVisibilityOption = 'all' | 'lock_region' | 'lock_office' | 'hidden';

interface IOpsDashboardSettingsJson {
  showDebug: boolean;
  simulateRole: UserRole | null;
  simulateUserId: string | null;
  tabVisibility: Record<string, Partial<Record<UserRole, TabVisibilityOption>>>;
  regionLocations: Record<string, string>;
  healthFlagThresholds: IHealthFlagThresholds;
  activeDonorStatuses: DonorStatus[];
  lastDonationActiveTimeoutDays: number;
  sglThresholdMl: number;
  activeDonorGrowthGoal: IHealthFlagRange;
  locationActiveGrowthGoal: IHealthFlagRange;
  locationPendingThreshold: IHealthFlagRange;
  attnScoreThreshold: IHealthFlagRange;
  attnScoreWeights: IAttnScoreWeights;
  locationUnitsDays: number;
  unitsPerEventDays: number;
  wcPrbcProductIds: number[];
  wcFfpSglProductIds: number[];
  wcFfpDblProductIds: number[];
  wcCryoProductIds: number[];
  wcMedicalSupplyProductIds: number[];
  wcRegionLocationMap: Record<string, string>;
}

const DEFAULT_SGL_THRESHOLD_ML = 420;

const DEFAULT_ACTIVE_DONOR_GROWTH_GOAL: IHealthFlagRange = {
  downFrom: 1,
  downTo: -10,
  warnFrom: 1,
  warnTo: 10,
  upFrom: 20,
  upTo: 10,
};

// Location pending threshold: lower is better, scale 0–10
// up = 0–2 (green), warn = 3–5 (yellow), down = 6–10 (pink)
const DEFAULT_LOCATION_PENDING_THRESHOLD: IHealthFlagRange = {
  upFrom: 0,
  upTo: 2,
  warnFrom: 3,
  warnTo: 5,
  downFrom: 6,
  downTo: 10,
};

// Location active donor growth goal (vs 90 days ago):
// up = >=10%, warn = 1–9%, down = 0% or decrease
const DEFAULT_LOCATION_ACTIVE_GROWTH_GOAL: IHealthFlagRange = {
  downFrom: 0,
  downTo: -10,
  warnFrom: 1,
  warnTo: 9,
  upFrom: 20,
  upTo: 10,
};

const DEFAULT_SETTINGS: IOpsDashboardSettingsJson = {
  showDebug: false,
  simulateRole: null,
  simulateUserId: null,
  tabVisibility: {},
  regionLocations: {},
  healthFlagThresholds: DEFAULT_HEALTH_FLAG_THRESHOLDS,
  activeDonorStatuses: DEFAULT_ACTIVE_DONOR_STATUSES,
  lastDonationActiveTimeoutDays: 90,
  sglThresholdMl: DEFAULT_SGL_THRESHOLD_ML,
  activeDonorGrowthGoal: DEFAULT_ACTIVE_DONOR_GROWTH_GOAL,
  locationActiveGrowthGoal: DEFAULT_LOCATION_ACTIVE_GROWTH_GOAL,
  locationPendingThreshold: DEFAULT_LOCATION_PENDING_THRESHOLD,
  attnScoreThreshold: DEFAULT_ATTN_SCORE_THRESHOLD,
  attnScoreWeights: DEFAULT_ATTN_SCORE_WEIGHTS,
  locationUnitsDays: 29,
  unitsPerEventDays: 90,
  wcPrbcProductIds: [411, 463],
  wcFfpSglProductIds: [502, 8179],
  wcFfpDblProductIds: [503, 504],
  wcCryoProductIds: [15186, 15874],
  wcMedicalSupplyProductIds: [764],
  wcRegionLocationMap: {},
};

const OpsDashboardSettings: FC = () => {
  const { t } = useTranslation('settings.OpsDashboardSettings');

  // Settings state
  const [showDebug, setShowDebug] = useState(DEFAULT_SETTINGS.showDebug);
  const [simulateRole, setSimulateRole] = useState<UserRole | null>(null);
  const [simulateUserId, setSimulateUserId] = useState<string | null>(null);
  const [tabVisibility, setTabVisibility] = useState<
    Record<string, Partial<Record<UserRole, TabVisibilityOption>>>
  >({});
  const [regionLocations, setRegionLocations] = useState<
    Record<string, string>
  >({});
  const [healthFlagThresholds, setHealthFlagThresholds] =
    useState<IHealthFlagThresholds>(DEFAULT_HEALTH_FLAG_THRESHOLDS);
  const [activeDonorStatuses, setActiveDonorStatuses] = useState<DonorStatus[]>(
    DEFAULT_ACTIVE_DONOR_STATUSES
  );
  const [lastDonationActiveTimeoutDays, setLastDonationActiveTimeoutDays] =
    useState<number>(DEFAULT_SETTINGS.lastDonationActiveTimeoutDays);
  const [sglThresholdMl, setSglThresholdMl] = useState<number>(
    DEFAULT_SGL_THRESHOLD_ML
  );
  const [activeDonorGrowthGoal, setActiveDonorGrowthGoal] =
    useState<IHealthFlagRange>(DEFAULT_ACTIVE_DONOR_GROWTH_GOAL);
  const [locationActiveGrowthGoal, setLocationActiveGrowthGoal] =
    useState<IHealthFlagRange>(DEFAULT_LOCATION_ACTIVE_GROWTH_GOAL);
  const [locationPendingThreshold, setLocationPendingThreshold] =
    useState<IHealthFlagRange>(DEFAULT_LOCATION_PENDING_THRESHOLD);
  const [attnScoreThreshold, setAttnScoreThreshold] =
    useState<IHealthFlagRange>(DEFAULT_ATTN_SCORE_THRESHOLD);
  const [attnScoreWeights, setAttnScoreWeights] = useState<IAttnScoreWeights>(
    DEFAULT_ATTN_SCORE_WEIGHTS
  );
  const [locationUnitsDays, setLocationUnitsDays] = useState<number>(29);
  const [unitsPerEventDays, setUnitsPerEventDays] = useState<number>(
    DEFAULT_SETTINGS.unitsPerEventDays
  );
  const [wcPrbcStr, setWcPrbcStr] = useState('411, 463');
  const [wcFfpSglStr, setWcFfpSglStr] = useState('502, 8179');
  const [wcFfpDblStr, setWcFfpDblStr] = useState('503, 504');
  const [wcCryoStr, setWcCryoStr] = useState('15186, 15874');
  const [wcMedSupplyStr, setWcMedSupplyStr] = useState('764');
  const [wcRegionLocationMap, setWcRegionLocationMap] = useState<
    Record<string, string>
  >({});

  // Fetch regions for location settings
  const { data: regionsData } = useGetRegionsQuery();
  const regions = useMemo(
    () =>
      (regionsData?.getRegions || [])
        .map((r) => ({ id: r.id, name: r.name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [regionsData]
  );

  // Fetch users by selected simulation role
  const [fetchSimulateUsers, simulateUsersQuery] = useGetUsersListLazyQuery();

  useEffect(() => {
    if (simulateRole && simulateRole !== UserRole.Admin) {
      fetchSimulateUsers({
        variables: {
          input: {
            filter: { roles: [simulateRole], isArchived: false },
            take: 200,
          },
        },
      });
    }
  }, [simulateRole, fetchSimulateUsers]);

  const simulateUserOptions = useMemo(() => {
    if (!simulateUsersQuery.data?.getUsersList?.data) return [];
    return simulateUsersQuery.data.getUsersList.data
      .map((user) => {
        const name = `${user.firstName} ${user.lastName}`;
        const regionText = user.productRegionPreference?.name || 'No region';
        const officeText = user.assignedOffice?.name || 'No office';
        return {
          value: user.id,
          searchLabel: `${name} ${regionText} ${officeText}`,
          label: (
            <span>
              {name} <Text type="secondary">({regionText})</Text>{' '}
              <Text type="secondary" style={{ fontSize: 12 }}>
                [{officeText}]
              </Text>
            </span>
          ),
        };
      })
      .sort((a, b) => a.searchLabel.localeCompare(b.searchLabel));
  }, [simulateUsersQuery.data]);

  // Track modifications
  const [isModified, setIsModified] = useState(false);
  const rawSettingsJsonRef = useRef<Record<string, unknown>>({});

  const markModified = useCallback(() => setIsModified(true), []);

  // Load settings
  const { loading: isLoading } = useQuery(GET_APP_SETTINGS_QUERY, {
    variables: { input: { settingsTab: SETTINGS_TAB } },
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.getAppSettings?.settingsJson) {
        const fullJson = data.getAppSettings.settingsJson as Record<
          string,
          unknown
        >;
        rawSettingsJsonRef.current = fullJson;
        const saved = data.getAppSettings
          .settingsJson as IOpsDashboardSettingsJson;
        if (saved.showDebug !== undefined) setShowDebug(saved.showDebug);
        if (saved.simulateRole !== undefined)
          setSimulateRole(saved.simulateRole);
        if (saved.simulateUserId !== undefined)
          setSimulateUserId(saved.simulateUserId);
        if (saved.tabVisibility) setTabVisibility(saved.tabVisibility);
        if (saved.regionLocations) setRegionLocations(saved.regionLocations);
        if (saved.healthFlagThresholds)
          setHealthFlagThresholds({
            ...DEFAULT_HEALTH_FLAG_THRESHOLDS,
            ...saved.healthFlagThresholds,
          });
        if (saved.activeDonorStatuses)
          setActiveDonorStatuses(saved.activeDonorStatuses);
        if (saved.lastDonationActiveTimeoutDays != null)
          setLastDonationActiveTimeoutDays(saved.lastDonationActiveTimeoutDays);
        if (saved.sglThresholdMl != null)
          setSglThresholdMl(saved.sglThresholdMl);
        if (saved.activeDonorGrowthGoal)
          setActiveDonorGrowthGoal({
            ...DEFAULT_ACTIVE_DONOR_GROWTH_GOAL,
            ...saved.activeDonorGrowthGoal,
          });
        if (saved.locationActiveGrowthGoal)
          setLocationActiveGrowthGoal({
            ...DEFAULT_LOCATION_ACTIVE_GROWTH_GOAL,
            ...saved.locationActiveGrowthGoal,
          });
        if (saved.locationPendingThreshold)
          setLocationPendingThreshold({
            ...DEFAULT_LOCATION_PENDING_THRESHOLD,
            ...saved.locationPendingThreshold,
          });
        if (saved.attnScoreThreshold)
          setAttnScoreThreshold({
            ...DEFAULT_ATTN_SCORE_THRESHOLD,
            ...saved.attnScoreThreshold,
          });
        if (saved.attnScoreWeights) {
          const merged = { ...DEFAULT_ATTN_SCORE_WEIGHTS };
          for (const k of Object.keys(
            saved.attnScoreWeights
          ) as (keyof IAttnScoreWeights)[]) {
            merged[k] = {
              ...DEFAULT_ATTN_SCORE_WEIGHTS[k],
              ...saved.attnScoreWeights[k],
            };
          }
          setAttnScoreWeights(merged);
        }
        if (saved.locationUnitsDays != null)
          setLocationUnitsDays(saved.locationUnitsDays);
        if (saved.unitsPerEventDays != null)
          setUnitsPerEventDays(saved.unitsPerEventDays);
        if (saved.wcPrbcProductIds)
          setWcPrbcStr(saved.wcPrbcProductIds.join(', '));
        if (saved.wcFfpSglProductIds)
          setWcFfpSglStr(saved.wcFfpSglProductIds.join(', '));
        if (saved.wcFfpDblProductIds)
          setWcFfpDblStr(saved.wcFfpDblProductIds.join(', '));
        if (saved.wcCryoProductIds)
          setWcCryoStr(saved.wcCryoProductIds.join(', '));
        if (saved.wcMedicalSupplyProductIds)
          setWcMedSupplyStr(saved.wcMedicalSupplyProductIds.join(', '));
        if (saved.wcRegionLocationMap)
          setWcRegionLocationMap(saved.wcRegionLocationMap);
      }
    },
  });

  // Save mutation
  const [saveAppSettings, { loading: isSaving }] = useMutation(
    SAVE_APP_SETTINGS_MUTATION,
    {
      onCompleted: () => {
        message.success('Settings saved successfully');
      },
      onError: (error) => {
        message.error(`Failed to save settings: ${error.message}`);
      },
    }
  );

  // Build current settings
  const currentSettings: IOpsDashboardSettingsJson = useMemo(
    () => ({
      showDebug,
      simulateRole,
      simulateUserId,
      tabVisibility,
      regionLocations,
      healthFlagThresholds,
      activeDonorStatuses,
      lastDonationActiveTimeoutDays,
      sglThresholdMl,
      activeDonorGrowthGoal,
      locationActiveGrowthGoal,
      locationPendingThreshold,
      attnScoreThreshold,
      attnScoreWeights,
      locationUnitsDays,
      unitsPerEventDays,
      wcPrbcProductIds: wcPrbcStr
        .replace(/\s/g, '')
        .split(',')
        .filter(Boolean)
        .map(Number)
        .filter((n) => !isNaN(n)),
      wcFfpSglProductIds: wcFfpSglStr
        .replace(/\s/g, '')
        .split(',')
        .filter(Boolean)
        .map(Number)
        .filter((n) => !isNaN(n)),
      wcFfpDblProductIds: wcFfpDblStr
        .replace(/\s/g, '')
        .split(',')
        .filter(Boolean)
        .map(Number)
        .filter((n) => !isNaN(n)),
      wcCryoProductIds: wcCryoStr
        .replace(/\s/g, '')
        .split(',')
        .filter(Boolean)
        .map(Number)
        .filter((n) => !isNaN(n)),
      wcMedicalSupplyProductIds: wcMedSupplyStr
        .replace(/\s/g, '')
        .split(',')
        .filter(Boolean)
        .map(Number)
        .filter((n) => !isNaN(n)),
      wcRegionLocationMap,
    }),
    [
      showDebug,
      simulateRole,
      simulateUserId,
      tabVisibility,
      regionLocations,
      healthFlagThresholds,
      activeDonorStatuses,
      lastDonationActiveTimeoutDays,
      sglThresholdMl,
      activeDonorGrowthGoal,
      locationActiveGrowthGoal,
      locationPendingThreshold,
      attnScoreThreshold,
      attnScoreWeights,
      locationUnitsDays,
      unitsPerEventDays,
      wcPrbcStr,
      wcFfpSglStr,
      wcFfpDblStr,
      wcCryoStr,
      wcMedSupplyStr,
      wcRegionLocationMap,
    ]
  );

  const handleSave = async () => {
    try {
      await saveAppSettings({
        variables: {
          input: {
            settingsTab: SETTINGS_TAB,
            settingsJson: {
              ...rawSettingsJsonRef.current,
              ...currentSettings,
            },
          },
        },
      });
      setIsModified(false);
    } catch (error) {
      console.error('Failed to save settings:', error);
    }
  };

  const handleTabVisibilityChange = (
    tabKey: string,
    role: UserRole,
    value: TabVisibilityOption
  ) => {
    markModified();
    setTabVisibility((prev) => ({
      ...prev,
      [tabKey]: {
        ...prev[tabKey],
        [role]: value,
      },
    }));
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Row gutter={[16, 16]}>
      {/* Debug Settings */}
      <Col span={24}>
        <Card>
          <Title level={5}>{t('debugTitle')}</Title>
          <Text type="secondary">{t('debugDescription')}</Text>

          <div style={{ marginTop: 16 }}>
            <Checkbox
              checked={showDebug}
              onChange={(e) => {
                markModified();
                setShowDebug(e.target.checked);
              }}
            >
              {t('showDebugLabel')}
            </Checkbox>
          </div>
        </Card>
      </Col>

      {/* Admin Simulate User Role */}
      <Col span={24}>
        <Card>
          <Title level={5}>Admin Simulate User Role</Title>
          <Text type="secondary">
            Simulate viewing the operations dashboard as a different user role.
            When a non-Admin role is selected, pick a specific user to
            impersonate their context (office, region, etc.). This is for
            testing role-based restrictions.
          </Text>

          <div style={{ marginTop: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              Simulate Role
            </Text>
            <Select
              value={simulateRole}
              onChange={(value: UserRole | null) => {
                markModified();
                setSimulateRole(value ?? null);
                setSimulateUserId(null);
              }}
              style={{ width: '100%' }}
              allowClear
              placeholder="No simulation (Admin)"
            >
              {ALL_USER_ROLES.map((role) => (
                <Select.Option key={role} value={role}>
                  {role}
                </Select.Option>
              ))}
            </Select>
          </div>

          {simulateRole && simulateRole !== UserRole.Admin && (
            <div style={{ marginTop: 16 }}>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>
                Simulate User
              </Text>
              <Text
                type="secondary"
                style={{ display: 'block', fontSize: 12, marginBottom: 8 }}
              >
                Pick a specific user with the {simulateRole} role to impersonate
                their context (office, region, etc.)
              </Text>
              <Select
                value={simulateUserId}
                onChange={(value: string) => {
                  markModified();
                  setSimulateUserId(value ?? null);
                }}
                style={{ width: '100%' }}
                allowClear
                showSearch
                loading={simulateUsersQuery.loading}
                placeholder="Select a user to simulate..."
                filterOption={(input, option) =>
                  ((option as Record<string, unknown>)?.searchLabel as string)
                    ?.toLowerCase()
                    .includes(input.toLowerCase()) ?? false
                }
                options={simulateUserOptions}
              />
            </div>
          )}
        </Card>
      </Col>

      {/* Tab Visibility */}
      <Col span={24}>
        <Card>
          <Title level={5}>{t('tabVisibilityTitle')}</Title>
          <Text type="secondary">{t('tabVisibilityDescription')}</Text>

          <div style={{ marginTop: 24 }}>
            {OPS_TAB_KEYS.map((tabKey) => (
              <div
                key={tabKey}
                style={{
                  marginBottom: 24,
                  paddingBottom: 24,
                  borderBottom: '1px solid #f0f0f0',
                }}
              >
                <Text
                  strong
                  style={{
                    display: 'block',
                    marginBottom: 12,
                    fontSize: 14,
                  }}
                >
                  {OPS_TAB_LABELS[tabKey]} Tab
                </Text>
                <Row gutter={[12, 8]}>
                  {ALL_USER_ROLES.filter((role) => role !== UserRole.Owner).map(
                    (role) => (
                      <Col key={role} xs={24} sm={12} md={8} lg={6}>
                        <div style={{ marginBottom: 4 }}>
                          <Text
                            type="secondary"
                            style={{ fontSize: 12, display: 'block' }}
                          >
                            {role}
                          </Text>
                          <Select
                            size="small"
                            value={tabVisibility[tabKey]?.[role] || 'all'}
                            onChange={(value: TabVisibilityOption) =>
                              handleTabVisibilityChange(tabKey, role, value)
                            }
                            style={{ width: '100%' }}
                          >
                            <Select.Option value="all">All</Select.Option>
                            <Select.Option value="lock_region">
                              Lock to user&apos;s region
                            </Select.Option>
                            <Select.Option value="lock_office">
                              Lock to user&apos;s office
                            </Select.Option>
                            <Select.Option value="hidden">
                              Hide {OPS_TAB_LABELS[tabKey]} tab
                            </Select.Option>
                          </Select>
                        </div>
                      </Col>
                    )
                  )}
                </Row>
              </div>
            ))}
          </div>
        </Card>
      </Col>

      {/* Health Flag Thresholds */}
      <Col span={24}>
        <Card>
          <Title level={5}>Health Flag Thresholds</Title>
          <Text type="secondary">
            Configure the threshold ranges for each health flag. Drag the
            handles to adjust the boundaries between UP (green), WARN (yellow),
            and DOWN (red) zones.
          </Text>

          <div style={{ marginTop: 24 }}>
            <HealthFlagSlider
              label="New Registration"
              description="Days since last new donor registration at the location. Lower is better."
              range={healthFlagThresholds.newRegistration}
              onChange={(r) => {
                markModified();
                setHealthFlagThresholds((prev) => ({
                  ...prev,
                  newRegistration: r,
                }));
              }}
              min={0}
              max={120}
              unit="d"
              lowerBetter
            />
            <HealthFlagSlider
              label="Pipeline Stall"
              description="Average days donors are stuck in Pending status at the location. Lower is better."
              range={healthFlagThresholds.pipelineStall}
              onChange={(r) => {
                markModified();
                setHealthFlagThresholds((prev) => ({
                  ...prev,
                  pipelineStall: r,
                }));
              }}
              min={0}
              max={120}
              unit="d"
              lowerBetter
            />
            <HealthFlagSlider
              label="Potential Registered"
              description="Percentage of registered donors vs total potential animals at location. Higher is better."
              range={healthFlagThresholds.potentialReg}
              onChange={(r) => {
                markModified();
                setHealthFlagThresholds((prev) => ({
                  ...prev,
                  potentialReg: r,
                }));
              }}
              min={0}
              max={100}
              unit="%"
              lowerBetter={false}
            />
            <HealthFlagSlider
              label="Unit MoM (Month-over-Month)"
              description="Percentage change in units collected vs previous period. Higher is better."
              range={healthFlagThresholds.unitMom}
              onChange={(r) => {
                markModified();
                setHealthFlagThresholds((prev) => ({ ...prev, unitMom: r }));
              }}
              min={-3}
              max={3}
              step={0.1}
              unit="%"
              lowerBetter={false}
            />
          </div>
        </Card>
      </Col>

      {/* Active Donor Statuses */}
      <Col span={24}>
        <Card>
          <Title level={5}>Active Donor Statuses</Title>
          <Text type="secondary">
            Select which donor statuses count as &quot;Active&quot; in the
            Recruiter tab summary cards and pipeline metrics.
          </Text>

          <div style={{ marginTop: 16 }}>
            <Checkbox.Group
              value={activeDonorStatuses}
              onChange={(values) => {
                markModified();
                setActiveDonorStatuses(values as DonorStatus[]);
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                  gap: 8,
                }}
              >
                {ALL_DONOR_STATUSES.map((status) => (
                  <Checkbox key={status} value={status}>
                    {status}
                  </Checkbox>
                ))}
              </div>
            </Checkbox.Group>
          </div>
        </Card>
      </Col>

      {/* Last Donation Active Timeout */}
      <Col span={24}>
        <Card>
          <Title level={5}>Last Donation Active Timeout</Title>
          <Text type="secondary">
            When estimating historical active donor counts, a donor with a
            donation within this many days of the period end is presumed active
            — unless their status was explicitly changed to a non-active status
            after the donation.
          </Text>

          <div style={{ marginTop: 16 }}>
            <InputNumber
              min={1}
              max={365}
              value={lastDonationActiveTimeoutDays}
              onChange={(value) => {
                markModified();
                setLastDonationActiveTimeoutDays(value ?? 90);
              }}
              addonAfter="days"
              style={{ width: 200 }}
            />
          </div>
        </Card>
      </Col>

      {/* SGL / DBL Threshold */}
      <Col span={24}>
        <Card>
          <Title level={5}>SGL / DBL Threshold</Title>
          <Text type="secondary">
            Donors with a <code>donationMLApprovedAmount</code> below this value
            are classified as Single Unit (SGL). At or above this value they are
            Double Unit (DBL).
          </Text>

          <div style={{ marginTop: 16 }}>
            <InputNumber
              min={100}
              max={1000}
              value={sglThresholdMl}
              onChange={(value) => {
                markModified();
                setSglThresholdMl(value ?? DEFAULT_SGL_THRESHOLD_ML);
              }}
              addonAfter="mL"
              style={{ width: 200 }}
            />
          </div>
        </Card>
      </Col>

      {/* Location Units Days */}
      <Col span={24}>
        <Card>
          <Title level={5}>Location Units per Days</Title>
          <Text type="secondary">
            The number of days used to calculate Units/Event on the Recruiter
            tab location strip. Units/Event = (units / period days) × this
            value. Default: 29.
          </Text>

          <div style={{ marginTop: 16 }}>
            <InputNumber
              min={1}
              max={365}
              value={locationUnitsDays}
              onChange={(value) => {
                markModified();
                setLocationUnitsDays(value ?? 29);
              }}
              addonAfter="days"
              style={{ width: 200 }}
            />
          </div>
        </Card>
      </Col>

      {/* Units per Event scope */}
      <Col span={24}>
        <Card>
          <Title level={5}>Units per Event Scope</Title>
          <Text type="secondary">
            Time window for the Recruiter tab location-strip Units/Event metric.
            &quot;All time&quot; uses the location&apos;s entire history.
            Otherwise, only donations within the rolling window are counted (in
            both numerator and denominator). Default: 90 days.
          </Text>

          <div style={{ marginTop: 16 }}>
            <Select
              value={unitsPerEventDays}
              onChange={(value) => {
                markModified();
                setUnitsPerEventDays(value);
              }}
              style={{ width: 240 }}
              options={[
                { value: 0, label: 'All time' },
                { value: 30, label: 'Last 30 days' },
                { value: 60, label: 'Last 60 days' },
                { value: 90, label: 'Last 90 days' },
                { value: 365, label: 'Last 365 days' },
              ]}
            />
          </div>
        </Card>
      </Col>

      {/* WC Product ID Mapping */}
      <Col span={24}>
        <Card>
          <Title level={5}>WC Product ID Mapping</Title>
          <Text type="secondary">
            Map WooCommerce product IDs to product classes for sales breakdown
            on the Overview tab. Enter comma-separated IDs.
          </Text>

          <div
            style={{
              marginTop: 16,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 12,
            }}
          >
            <div>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>
                pRBC
              </Text>
              <Input
                value={wcPrbcStr}
                onChange={(e) => {
                  markModified();
                  setWcPrbcStr(e.target.value);
                }}
                placeholder="411, 463"
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>
                FFP SGL
              </Text>
              <Input
                value={wcFfpSglStr}
                onChange={(e) => {
                  markModified();
                  setWcFfpSglStr(e.target.value);
                }}
                placeholder="502, 8179"
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>
                FFP DBL
              </Text>
              <Input
                value={wcFfpDblStr}
                onChange={(e) => {
                  markModified();
                  setWcFfpDblStr(e.target.value);
                }}
                placeholder="503, 504"
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>
                Cryo
              </Text>
              <Input
                value={wcCryoStr}
                onChange={(e) => {
                  markModified();
                  setWcCryoStr(e.target.value);
                }}
                placeholder="15186, 15874"
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>
                Medical Supply
              </Text>
              <Input
                value={wcMedSupplyStr}
                onChange={(e) => {
                  markModified();
                  setWcMedSupplyStr(e.target.value);
                }}
                placeholder="764"
                style={{ width: '100%' }}
              />
            </div>
          </div>
        </Card>
      </Col>

      {/* WC Region → Location Mapping */}
      <Col span={24}>
        <Card>
          <Title level={5}>WC Region → Location Mapping</Title>
          <Text type="secondary">
            Map each DROPS region to the WooCommerce ship location names used in
            orders. Enter comma-separated location names. This enables the
            region filter on WooCommerce sales data.
          </Text>

          <div style={{ marginTop: 16 }}>
            {regions.map((region) => (
              <div key={region.id} style={{ marginBottom: 12 }}>
                <Text
                  strong
                  style={{ display: 'block', marginBottom: 4, fontSize: 13 }}
                >
                  {region.name}
                </Text>
                <Input
                  value={wcRegionLocationMap[region.id] || ''}
                  onChange={(e) => {
                    markModified();
                    setWcRegionLocationMap((prev) => ({
                      ...prev,
                      [region.id]: e.target.value,
                    }));
                  }}
                  placeholder={`e.g. Manassas, Sterling`}
                  style={{ maxWidth: 400 }}
                />
              </div>
            ))}
          </div>
        </Card>
      </Col>

      {/* Active Donor Growth Goal */}
      <Col span={24}>
        <Card>
          <Title level={5}>Active Donor Growth Goal</Title>
          <Text type="secondary">
            Color-code the historical active donor count on the Recruiter tab
            based on the percentage change from the previous period. Higher
            growth is better.
          </Text>

          <div style={{ marginTop: 24 }}>
            <HealthFlagSlider
              label="Growth %"
              description="Percentage change in active donors vs end of previous period. Positive = growth."
              range={activeDonorGrowthGoal}
              onChange={(r) => {
                markModified();
                setActiveDonorGrowthGoal(r);
              }}
              min={-10}
              max={20}
              unit="%"
              lowerBetter={false}
            />
          </div>
        </Card>
      </Col>

      {/* Location Active Growth Goal */}
      <Col span={24}>
        <Card>
          <Title level={5}>Location Active Donor Growth Goal</Title>
          <Text type="secondary">
            Color-code the active donor growth trend in the location strip (vs.
            90 days ago). 10%+ growth = green, 1–9% = yellow, 0% or decrease =
            pink. Scale: -10% to +20%.
          </Text>

          <div style={{ marginTop: 24 }}>
            <HealthFlagSlider
              label="Growth % (90d)"
              description="Percentage change in active donors at this location vs 90 days ago."
              range={locationActiveGrowthGoal}
              onChange={(r) => {
                markModified();
                setLocationActiveGrowthGoal(r);
              }}
              min={-10}
              max={20}
              unit="%"
              lowerBetter={false}
            />
          </div>
        </Card>
      </Col>

      {/* Location Pending Threshold */}
      <Col span={24}>
        <Card>
          <Title level={5}>Location Pending Threshold</Title>
          <Text type="secondary">
            Color-code the Pending Donors count in the location strip. Lower is
            better (0–10 scale). Drag handles to set boundaries between green,
            yellow, and pink zones.
          </Text>

          <div style={{ marginTop: 24 }}>
            <HealthFlagSlider
              label="Pending count"
              description="Number of donors currently in Pending status at this location."
              range={locationPendingThreshold}
              onChange={(r) => {
                markModified();
                setLocationPendingThreshold(r);
              }}
              min={0}
              max={10}
              unit=""
              lowerBetter
            />
          </div>
        </Card>
      </Col>

      {/* Attn Score Threshold */}
      <Col span={24}>
        <Card>
          <Title level={5}>Attn Score Threshold</Title>
          <Text type="secondary">
            Color-code the Attention Score (0–20) on the Recruiter tab location
            strip. Lower scores are better (0 = green, 20 = pink). Drag the
            handles to adjust the boundaries between UP (green), WARN (yellow),
            and DOWN (pink/red) zones.
          </Text>

          <div style={{ marginTop: 24 }}>
            <HealthFlagSlider
              label="Attn Score"
              description="Location attention score from 0–20. Lower is better."
              range={attnScoreThreshold}
              onChange={(r) => {
                markModified();
                setAttnScoreThreshold(r);
              }}
              min={0}
              max={20}
              unit=""
              lowerBetter
            />
          </div>
        </Card>
      </Col>

      {/* Attn Score Weights */}
      <Col span={24}>
        <Card>
          <Title level={5}>Attn Score Weights</Title>
          <Text type="secondary">
            Points added to the Attention Score (0–20) per health flag status.
            Negative values reduce the score (e.g. a green flag can lower the
            total). Score is clamped to 0–20.
          </Text>

          <div style={{ marginTop: 16 }}>
            {(
              [
                { key: 'newRegistration', label: 'New Registration' },
                { key: 'pipelineStall', label: 'Pipeline Stall' },
                { key: 'potentialReg', label: 'Potential Registered' },
                { key: 'unitMom', label: 'Unit MoM' },
              ] as { key: keyof IAttnScoreWeights; label: string }[]
            ).map(({ key, label }) => (
              <div
                key={key}
                style={{
                  marginBottom: 16,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                }}
              >
                <Text style={{ width: 180, flexShrink: 0 }}>{label}</Text>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Text style={{ color: '#E8008A', minWidth: 70 }}>
                    Pink pts
                  </Text>
                  <InputNumber
                    min={-20}
                    max={20}
                    value={attnScoreWeights[key].downWeight}
                    onChange={(val) => {
                      markModified();
                      setAttnScoreWeights((prev) => ({
                        ...prev,
                        [key]: { ...prev[key], downWeight: val ?? 0 },
                      }));
                    }}
                    style={{ width: 70 }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Text style={{ color: '#c6c300', minWidth: 80 }}>
                    Yellow pts
                  </Text>
                  <InputNumber
                    min={-20}
                    max={20}
                    value={attnScoreWeights[key].warnWeight}
                    onChange={(val) => {
                      markModified();
                      setAttnScoreWeights((prev) => ({
                        ...prev,
                        [key]: { ...prev[key], warnWeight: val ?? 0 },
                      }));
                    }}
                    style={{ width: 70 }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Text style={{ color: '#00E5A0', minWidth: 75 }}>
                    Green pts
                  </Text>
                  <InputNumber
                    min={-20}
                    max={20}
                    value={attnScoreWeights[key].upWeight}
                    onChange={(val) => {
                      markModified();
                      setAttnScoreWeights((prev) => ({
                        ...prev,
                        [key]: { ...prev[key], upWeight: val ?? 0 },
                      }));
                    }}
                    style={{ width: 70 }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </Col>

      {/* Region Locations */}
      <Col span={24}>
        <Card>
          <Title level={5}>Region Locations</Title>
          <Text type="secondary">
            Set the display location text for each region (e.g. &quot;Richmond,
            VA&quot;). This is shown in the recruiter tab header.
          </Text>

          <div style={{ marginTop: 16 }}>
            {regions.map((region) => (
              <div key={region.id} style={{ marginBottom: 12 }}>
                <Text
                  strong
                  style={{ display: 'block', marginBottom: 4, fontSize: 13 }}
                >
                  {region.name} Location
                </Text>
                <Input
                  value={regionLocations[region.id] || ''}
                  onChange={(e) => {
                    markModified();
                    setRegionLocations((prev) => ({
                      ...prev,
                      [region.id]: e.target.value,
                    }));
                  }}
                  placeholder={`e.g. ${region.name}`}
                  style={{ maxWidth: 400 }}
                />
              </div>
            ))}
          </div>
        </Card>
      </Col>

      {/* Save Button */}
      <Col span={24}>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSave}
          loading={isSaving}
          disabled={!isModified}
          size="large"
        >
          {t('saveSettings')}{' '}
          {isModified && (
            <Text type="warning" style={{ marginLeft: 8, fontSize: 12 }}>
              ({t('notSaved')})
            </Text>
          )}
        </Button>
      </Col>

      {/* Settings JSON */}
      <Col span={24}>
        <Collapse>
          <Collapse.Panel
            key="settings-json"
            header={
              <Space>
                <span>Settings JSON</span>
                {isModified && (
                  <span style={{ color: '#f31a25', fontSize: 12 }}>
                    ({t('notSaved')})
                  </span>
                )}
              </Space>
            }
          >
            <pre
              style={{
                backgroundColor: '#f5f5f5',
                padding: 12,
                borderRadius: 4,
                fontSize: 12,
                margin: 0,
              }}
            >
              {JSON.stringify(
                { ...rawSettingsJsonRef.current, ...currentSettings },
                null,
                2
              )}
            </pre>
          </Collapse.Panel>
        </Collapse>
      </Col>
    </Row>
  );
};

export default OpsDashboardSettings;
